import { useState, useRef, useEffect } from 'react';

// 사진 위에 손가락으로 칠하는 캔버스 — 부분 편집(EditRegionModal)과 평면도 방 고르기
// (PlanRoomPicker)가 같이 쓴다. <img ref={imgRef}> 위에 <canvas ref={canvasRef}> 를 겹치고
// handlers 를 canvas 에 펼치면 된다. 결과는 composeOverlayBlob 이 "칠한 자리를 빨갛게 덮은
// 사본" 으로 굽는다 — 이진 마스크는 모델이 잘 못 읽지만 빨간 칠은 안정적으로 읽는다.
export const BRUSH_MIN = 10;
export const BRUSH_MAX = 120;

export function usePaintMask({ src, defaultBrush = 65 }) {
  const [hasMask, setHasMask] = useState(false);
  const [brushSize, setBrushSize] = useState(defaultBrush);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPt = useRef(null);
  // 칠한 획 목록 — 되돌리기는 마지막 획을 빼고 다시 그린다. (예전 지우개는 칠한
  // 선택만 지웠는데, 사진을 지우는 도구로 읽혀 빈 사진을 문지르며 헤맸다.)
  const strokes = useRef([]);

  useEffect(() => {
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas) return;
    const sync = () => {
      canvas.width = img.naturalWidth || 1024;
      canvas.height = img.naturalHeight || 1024;
    };
    if (img.complete && img.naturalWidth) sync();
    img.addEventListener('load', sync);
    return () => img.removeEventListener('load', sync);
  }, [src]);

  const getPt = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: ((clientX - rect.left) / rect.width) * canvas.width,
      y: ((clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const drawSeg = (a, b, size = brushSize) => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.strokeStyle = 'rgba(255, 80, 80, 0.35)';
    ctx.lineWidth = size;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  };

  const onDown = (e) => {
    drawing.current = true;
    const pt = getPt(e);
    lastPt.current = pt;
    strokes.current.push({ size: brushSize, pts: [pt] });
    drawSeg(pt, pt);
    setHasMask(true);
  };
  const onMove = (e) => {
    if (!drawing.current) return;
    const pt = getPt(e);
    drawSeg(lastPt.current, pt);
    strokes.current[strokes.current.length - 1]?.pts.push(pt);
    lastPt.current = pt;
  };
  const onUp = () => {
    drawing.current = false;
    lastPt.current = null;
  };

  const undoStroke = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    strokes.current.pop();
    strokes.current.forEach(({ size, pts }) => {
      pts.forEach((p, i) => drawSeg(pts[Math.max(0, i - 1)], p, size));
    });
    setHasMask(strokes.current.length > 0);
  };

  const clearMask = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    strokes.current = [];
    setHasMask(false);
  };

  // 원본 위에 칠한 자리를 빨갛게 덮은 사본 (긴 변 1024px JPEG).
  // tint 0 이면 완전한 빨강 (부분 편집), 0~1 이면 그만큼 원본이 비친다 — 평면도 방
  // 고르기는 칠한 방의 창·문이 보여야 해서 반투명으로 쓴다.
  const composeOverlayBlob = async ({ tint = 0 } = {}) => {
    const overlayCanvas = canvasRef.current;
    const sw = overlayCanvas.width, sh = overlayCanvas.height;
    const MAX = 1024;
    const scale = Math.min(1, MAX / Math.max(sw, sh));
    const w = Math.round(sw * scale);
    const h = Math.round(sh * scale);

    const out = document.createElement('canvas');
    out.width = w; out.height = h;
    const octx = out.getContext('2d');
    octx.drawImage(imgRef.current, 0, 0, w, h);
    const tmp = document.createElement('canvas');
    tmp.width = w; tmp.height = h;
    tmp.getContext('2d').drawImage(overlayCanvas, 0, 0, w, h);

    const overlayData = tmp.getContext('2d').getImageData(0, 0, w, h).data;
    const composed = octx.getImageData(0, 0, w, h);
    const cd = composed.data;
    for (let i = 0; i < overlayData.length; i += 4) {
      if (overlayData[i + 3] > 10) {
        cd[i] = Math.round(255 * (1 - tint) + cd[i] * tint);
        cd[i + 1] = Math.round(30 * (1 - tint) + cd[i + 1] * tint);
        cd[i + 2] = Math.round(30 * (1 - tint) + cd[i + 2] * tint);
      }
    }
    octx.putImageData(composed, 0, 0);
    return new Promise(resolve => out.toBlob(resolve, 'image/jpeg', 0.85));
  };

  return {
    imgRef,
    canvasRef,
    hasMask,
    brushSize,
    setBrushSize,
    undoStroke,
    clearMask,
    composeOverlayBlob,
    handlers: {
      onMouseDown: onDown,
      onMouseMove: onMove,
      onMouseUp: onUp,
      onMouseLeave: onUp,
      onTouchStart: onDown,
      onTouchMove: onMove,
      onTouchEnd: onUp,
    },
  };
}
